/**
 * SiblingTimeline (13-25): every audit sibling row of the record in plain words, newest first (`lib/recordTimeline`).
 * One heading per save (the rows that share an `[a:<id>]` tag), who and when, then one line per row. Who: the
 * display name from one User Information List read (`useDisplayNames`), else the UPN. When: Melbourne time.
 */

import { useMemo } from 'react';
import { useDisplayNames } from '../../hooks/useDisplayNames';
import { formatInstant } from '../../lib/fieldValues';
import { buildTimeline, timelineUpns } from '../../lib/recordTimeline';
import type { TimelineList } from '../../lib/recordTimeline';
import type { SiblingRow } from '../../services/recordService';
import type { Actor } from '../../types/actionRegister';
import Lines from './Lines';
import styles from './record.module.css';

export interface SiblingTimelineProps {
  /** A register, or 13-32's Internal Audits list. */
  register: TimelineList;
  rows: readonly SiblingRow[];
  actor: Actor | null;
}

export default function SiblingTimeline({ register, rows, actor }: SiblingTimelineProps) {
  const groups = useMemo(() => buildTimeline(register, rows), [register, rows]);
  const names = useDisplayNames(timelineUpns(groups), actor);

  if (groups.length === 0) {
    return <p className={styles.empty}>No audit rows yet.</p>;
  }

  return (
    <ol className={styles.timeline}>
      {groups.map((g) => {
        const upn = (g.actorUpn ?? '').trim().toLowerCase();
        const who = names.get(upn) ?? (g.actorUpn || 'Someone');
        return (
          <li key={g.key} className={styles.entry}>
            <p className={styles.entryHead}>
              <span className={styles.entryHeading}>{g.heading}</span>
              <span className={styles.entryMeta}>
                {who}
                {g.at ? ` · ${formatInstant(g.at)}` : ''}
              </span>
            </p>
            <ul className={styles.entryLines}>
              {g.lines.map((l) => (
                <li key={l.id}>
                  {l.kind === 'entry' && g.lines.length === 1 && l.label === g.heading ? null : <span>{l.label}</span>}
                  {l.text && (
                    <span className={styles.entryText}>
                      <Lines text={l.text} />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ol>
  );
}
